/**
 * "Don't credit my inviter", in Advanced privacy (GH-864e; plan §1.3's exclusion list).
 *
 * A member who joined through somebody's invite can keep their gifts out of that person's points —
 * and out of everyone further up the chain, since influence credits every ancestor (owner
 * amendment 4). It is a member preference (`countForInviter`), so it is drawn only for a member who
 * joined through an invite, and never while `FEATURE_MEMBERSHIP` is off. The switch is the inverse of the stored flag: on means
 * "don't count".
 *
 * Unlike "Anonymous to all organizations" above it, this changes nothing anybody else can see
 * about the donor and hurts nobody if flipped by mistake, so it is one switch, not three steps.
 */
import { useState } from "react";
import { MEMBERSHIP_COPY } from "@givehere/contracts";
import { DonorApiError, updateMembershipPreferences } from "./donor-session.js";
import { Explain } from "./Explain.js";
import { useDonorMembership } from "./membership-hooks.js";

export function InviterCreditSetting({ token }: { token: string }) {
  const { membership, setMembership } = useDonorMembership(token);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const member = membership?.state === "member" ? membership.member : null;
  // Only somebody who has an inviter has anyone to keep their gifts from.
  if (!member || member.joinedVia !== "invite") return null;

  const dontCount = !member.countForInviter;
  return (
    <section className="privacy-setting" aria-labelledby="inviter-credit-title">
      <h3 id="inviter-credit-title" className="paymethods__title">
        {MEMBERSHIP_COPY.yourInfluence}
      </h3>
      <label className="toggle">
        <input
          type="checkbox"
          role="switch"
          checked={dontCount}
          aria-checked={dontCount}
          disabled={saving}
          onChange={() => {
            setError(null);
            setSaving(true);
            updateMembershipPreferences(token, { countForInviter: dontCount })
              .then(setMembership)
              .catch((caught: unknown) =>
                setError(
                  caught instanceof DonorApiError ? caught.message : "That didn't save. Try again.",
                ),
              )
              .finally(() => setSaving(false));
          }}
        />
        <span>{MEMBERSHIP_COPY.dontCreditInviter}</span>
      </label>
      <Explain
        className="privacy-setting__more"
        label="What this changes"
        title={MEMBERSHIP_COPY.dontCreditInviter}
      >
        <p>{MEMBERSHIP_COPY.dontCreditInviterExplanation}</p>
      </Explain>
      {error && (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
